import React, { useState } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    Modal,
    Dialog,
    DialogContent,
    List, ListItem, ListItemIcon, ListItemText,
    InputAdornment,
    IconButton
} from '@mui/material';
import { useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import { validatePassword } from '../validations.ts';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { UserObject } from '../../../interfaces/Management.interface.ts';
import { createUserFormDataAtom } from '../../../Services/_states/index.js';

interface ChangePasswordModalProps {
    open: boolean;
    onClose: () => void;
    onChangePassword: (password: string) => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ open, onClose, onChangePassword }) => {
    const loggedInUser = useRecoilValue(userInformationAtom);
    const userFormData: UserObject = useRecoilValue(createUserFormDataAtom);

    const [formData, setFormData] = useState({
        password: '',
        confirm_password: '',
    });
    const [validationErrors, setValidationErrors] = useState({
        password: '',
        confirm_password: '',
    });
    const [showPassword, setShowPassword] = useState(false);

    const [passwordRules, setPasswordRules] = useState({
        minLength: false,
        uppercase: false,
        lowercase: false,
        number: false,
        specialChar: false,
        noCommonWords: true,
        noUsernameSubstring: false,
        // expiryEnforced: true,
        // lockoutEnforced: true,
        // passwordReuseLimitExceeded: false
    });

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const errors: any = {};
        const password = formData?.password;
        const { isValid, rules } = validatePassword(password, userFormData?.username || "");
        if (!isValid) {
            errors.password = "Password does not meet all reuirements"
        }

        if (password !== formData?.confirm_password) {
            errors.confirm_password = "Passwords do not match.";
        }

        setValidationErrors(errors);

        if (Object.keys(errors).length === 0) {
            onChangePassword(password);
            onClose();
        }
    };

    const handleInputChange = (field: string, value: string) => {
        setFormData({ ...formData, [field]: value });
        if (field === "password") {
            const { isValid, rules } = validatePassword(value, userFormData?.username || "");
            setPasswordRules(rules);
            setValidationErrors((prevErrors) => ({
                ...prevErrors,
                password: isValid ? "" : "Password does not meet all requirements",
            }));
        }

    };



    return (
        <>
            <Modal open={open} onClose={onClose}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        borderRadius: '8px',
                        p: 3,
                        width: 400,
                        maxHeight: '90vh',
                        overflowY: 'auto',
                    }}
                >
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Change Password
                    </Typography>
                    <form onSubmit={handleSubmit}>
                        <TextField
                            label={
                                <Typography component="span">
                                    New Password <span style={{ color: 'red' }}>*</span>
                                </Typography>
                            }
                            type={showPassword ? 'text' : 'password'}
                            fullWidth
                            value={formData.password}
                            onChange={(e) => {
                                handleInputChange("password", e.target.value)
                                setValidationErrors({ ...validationErrors, password: '' });
                            }}
                            sx={{ marginBottom: 2 }}
                            margin="dense"
                            variant="outlined"
                            size="small"
                            error={!!validationErrors.password}
                            helperText={validationErrors.password}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}

                        />
                        <Typography variant="subtitle1" style={{ marginTop: "5px" }}>
                            Password must meet the following requirements:
                        </Typography>
                        <Box mt={1} mb={1}>
                            <Typography variant="caption" display="flex" alignItems="center" color={passwordRules?.minLength ? 'success.main' : 'error.main'}>
                                {passwordRules?.minLength ? <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} /> : <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />}
                                At least 12 characters
                            </Typography>
                            <Typography variant="caption" display="flex" alignItems="center" color={passwordRules?.uppercase ? 'success.main' : 'error.main'}>
                                {passwordRules?.uppercase ? <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} /> : <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />}
                                At least one uppercase letter (A-Z)
                            </Typography>
                            <Typography variant="caption" display="flex" alignItems="center" color={passwordRules?.lowercase ? 'success.main' : 'error.main'}>
                                {passwordRules?.lowercase ? <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} /> : <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />}
                                At least one lowercase letter (a-z)
                            </Typography>
                            <Typography variant="caption" display="flex" alignItems="center" color={passwordRules?.number ? 'success.main' : 'error.main'}>
                                {passwordRules?.number ? <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} /> : <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />}
                                At least one number (0-9)
                            </Typography>
                            <Typography variant="caption" display="flex" alignItems="center" color={passwordRules?.specialChar ? 'success.main' : 'error.main'}>
                                {passwordRules?.specialChar ? <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} /> : <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />}
                                At least one special character (@$!%*?&)
                            </Typography>
                            <Typography variant="caption" display="flex" alignItems="center" color={passwordRules?.noCommonWords ? 'success.main' : 'error.main'}>
                                {passwordRules?.noCommonWords ? <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} /> : <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />}
                                Must not include common words (e.g., 'password', 'admin')
                            </Typography>
                            <Typography variant="caption" display="flex" alignItems="center" color={passwordRules?.noUsernameSubstring ? 'success.main' : 'error.main'}>
                                {passwordRules?.noUsernameSubstring ? <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} /> : <CancelIcon fontSize="small" sx={{ mr: 0.5 }} />}
                                Must not contain your username
                            </Typography>
                        </Box>
                        {/* <List dense>
                            <ListItem>
                                <ListItemIcon>
                                    {passwordRules.minLength ? (
                                        <CheckCircleIcon color="success" />
                                    ) : (
                                        <CancelIcon color="error" />
                                    )}
                                </ListItemIcon>
                                <ListItemText primary="At least 12 characters long" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    {passwordRules.uppercase ? (
                                        <CheckCircleIcon color="success" />
                                    ) : (
                                        <CancelIcon color="error" />
                                    )}
                                </ListItemIcon>
                                <ListItemText primary="At least one uppercase letter (A–Z)" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    {passwordRules.lowercase ? (
                                        <CheckCircleIcon color="success" />
                                    ) : (
                                        <CancelIcon color="error" />
                                    )}
                                </ListItemIcon>
                                <ListItemText primary="At least one lowercase letter (a–z)" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    {passwordRules.number ? (
                                        <CheckCircleIcon color="success" />
                                    ) : (
                                        <CancelIcon color="error" />
                                    )}
                                </ListItemIcon>
                                <ListItemText primary="At least one number (0–9)" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    {passwordRules.specialChar ? (
                                        <CheckCircleIcon color="success" />
                                    ) : (
                                        <CancelIcon color="error" />
                                    )}
                                </ListItemIcon>
                                <ListItemText primary="At least one special character (e.g., !@#$%^&*())" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    {passwordRules?.noCommonWords ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
                                </ListItemIcon>
                                <ListItemText primary="Must not include common words (e.g., 'password', 'admin')" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    {passwordRules?.noUsernameSubstring ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
                                </ListItemIcon>
                                <ListItemText primary="Must not contain your username" />
                            </ListItem>
                        </List> */}

                        <TextField
                            label={
                                <Typography component="span">
                                    Confirm Password <span style={{ color: 'red' }}>*</span>
                                </Typography>
                            }
                            type="password"
                            fullWidth
                            value={formData.confirm_password}
                            onChange={(e) => {
                                setFormData({ ...formData, confirm_password: e.target.value });
                                setValidationErrors({ ...validationErrors, confirm_password: '' });
                            }}
                            sx={{ marginBottom: 2 }}
                            margin="dense"
                            variant="outlined"
                            size="small"
                            error={!!validationErrors.confirm_password}
                            helperText={validationErrors.confirm_password}
                        />
                        <Box display="flex" flexDirection="column" alignItems="stretch" gap={2}>
                            <Box display="flex" justifyContent="flex-end" gap={2}>
                                <Button variant="outlined" onClick={onClose} size="small">
                                    Cancel
                                </Button>
                                <Button variant="contained" type="submit" color="primary" size="small"
                                >
                                    Change Password
                                </Button>
                            </Box>
                        </Box>
                    </form>
                </Box>
            </Modal>
        </>
    );
};

export default ChangePasswordModal;